import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { Plus, Pencil, Trash2, X, Check } from 'lucide-react';

const fmt = (v) =>
  v != null ? `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '—';

const fmtDate = (d) => {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
};

const EMPTY_FORM = { descricao: '', valor: '', data: '' };

const S = {
  heading: { fontSize: 22, fontWeight: 700, color: '#f6c90e', marginBottom: 4 },
  sub:     { fontSize: 13, color: '#475569', marginBottom: 24 },
  toolbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 },
  wrap:    { background: '#0e1420', border: '1px solid #1a2035', borderRadius: 12, overflowX: 'auto', overflowY: 'auto', maxHeight: 400 },
  table:   { width: '100%', borderCollapse: 'collapse' },
  th:      { padding: '10px 16px', fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: 1,
             borderBottom: '1px solid #1a2035', fontWeight: 700, textAlign: 'left' },
  td:      { padding: '12px 16px', fontSize: 13, color: '#cbd5e1', borderBottom: '1px solid #0f1117' },
  btnAdd:  { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8,
             background: '#f6c90e', color: '#080c14', fontWeight: 700, fontSize: 13, cursor: 'pointer', border: 'none' },
  btnEdit: { display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 6,
             background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.3)', color: '#60a5fa',
             fontSize: 12, cursor: 'pointer' },
  btnDel:  { display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 6,
             background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', color: '#f87171',
             fontSize: 12, cursor: 'pointer' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 200,
             display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal:   { background: '#0e1420', border: '1px solid #1a2035', borderRadius: 12, padding: 28, width: 440, maxWidth: '95vw' },
  mTitle:  { fontSize: 16, fontWeight: 700, color: '#f6c90e', marginBottom: 20 },
  label:   { fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, display: 'block', marginBottom: 5 },
  input:   { width: '100%', background: '#080c14', border: '1px solid #1a2035', borderRadius: 7,
             padding: '9px 12px', color: '#e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 14 },
  mFooter: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 },
  btnCancel: { padding: '8px 16px', borderRadius: 8, background: 'transparent', border: '1px solid #1a2035', color: '#64748b', fontSize: 13, cursor: 'pointer' },
  btnSave:   { padding: '8px 18px', borderRadius: 8, background: '#f6c90e', color: '#080c14', fontWeight: 700, fontSize: 13, cursor: 'pointer', border: 'none' },
  totalRow:  { padding: '12px 16px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, borderTop: '2px solid #1a2035', background: '#0b111e' },
};

export default function CustosContabil() {
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(null); // null | { mode: 'create' } | { mode: 'edit', item }
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // id a deletar

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/custos-contabil/');
      setRegistros(r.data);
    } catch {
      setRegistros([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setModal({ mode: 'create' });
  };

  const openEdit = (item) => {
    setForm({ descricao: item.descricao, valor: item.valor, data: item.data });
    setModal({ mode: 'edit', item });
  };

  const closeModal = () => { setModal(null); setSaving(false); };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.descricao.trim() || !form.valor || !form.data) {
      alert('Preencha todos os campos.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        descricao: form.descricao.trim(),
        valor: parseFloat(String(form.valor).replace(',', '.')),
        data: form.data,
      };
      if (modal.mode === 'create') {
        const r = await api.post('/custos-contabil/', payload);
        setRegistros(prev => [r.data, ...prev]);
      } else {
        const r = await api.patch(`/custos-contabil/${modal.item.id}/`, payload);
        setRegistros(prev => prev.map(x => x.id === modal.item.id ? r.data : x));
      }
      closeModal();
    } catch (e) {
      alert('Erro ao salvar: ' + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const id = confirmDel;
    setConfirmDel(null);
    try {
      await api.delete(`/custos-contabil/${id}/`);
      setRegistros(prev => prev.filter(x => x.id !== id));
    } catch (e) {
      alert('Erro ao deletar: ' + (e.message || 'tente novamente'));
    }
  };

  const total = registros.reduce((s, r) => s + parseFloat(r.valor || 0), 0);

  return (
    <div>
      <div style={S.heading}>Custos da Empresa</div>
      <div style={S.sub}>Contas a pagar da empresa — salários, energia, água e outros.</div>

      <div style={S.toolbar}>
        <span style={{ fontSize: 13, color: '#475569' }}>
          {registros.length} registro{registros.length !== 1 ? 's' : ''}
        </span>
        <button style={S.btnAdd} onClick={openCreate}>
          <Plus size={15} /> Novo Registro
        </button>
      </div>

      <div style={S.wrap}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Descrição</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Valor</th>
              <th style={S.th}>Data</th>
              <th style={S.th}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} style={{ ...S.td, textAlign: 'center', color: '#475569', padding: 32 }}>Carregando...</td></tr>
            ) : registros.length === 0 ? (
              <tr><td colSpan={4} style={{ ...S.td, textAlign: 'center', color: '#475569', padding: 32 }}>Nenhum registro cadastrado.</td></tr>
            ) : (
              registros.map(r => (
                <tr key={r.id}
                  onMouseEnter={e => e.currentTarget.style.background = '#111827'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={S.td}>{r.descricao}</td>
                  <td style={{ ...S.td, textAlign: 'right', color: '#f87171', fontWeight: 600 }}>
                    {fmt(r.valor)}
                  </td>
                  <td style={S.td}>{fmtDate(r.data)}</td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={S.btnEdit} onClick={() => openEdit(r)}>
                        <Pencil size={12} /> Editar
                      </button>
                      <button style={S.btnDel} onClick={() => setConfirmDel(r.id)}>
                        <Trash2 size={12} /> Deletar
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {!loading && registros.length > 0 && (
          <div style={S.totalRow}>
            <span style={{ fontSize: 12, color: '#475569' }}>Total:</span>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#f87171' }}>{fmt(total)}</span>
          </div>
        )}
      </div>

      {/* Modal criar / editar */}
      {modal && (
        <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
          <div style={S.modal}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={S.mTitle}>{modal.mode === 'create' ? 'Novo Registro' : 'Editar Registro'}</div>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>

            <label style={S.label}>Descrição</label>
            <input style={S.input} type="text" placeholder="Ex: Salário João, Conta de Energia..."
              value={form.descricao} onChange={e => set('descricao', e.target.value)} autoFocus />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={S.label}>Valor (R$)</label>
                <input style={S.input} type="number" step="0.01" min="0" placeholder="0,00"
                  value={form.valor} onChange={e => set('valor', e.target.value)} />
              </div>
              <div>
                <label style={S.label}>Data</label>
                <input style={S.input} type="date"
                  value={form.data} onChange={e => set('data', e.target.value)} />
              </div>
            </div>

            <div style={S.mFooter}>
              <button style={S.btnCancel} onClick={closeModal}>Cancelar</button>
              <button style={S.btnSave} onClick={handleSave} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmar deletar */}
      {confirmDel && (
        <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) setConfirmDel(null); }}>
          <div style={{ ...S.modal, width: 360 }}>
            <div style={{ fontSize: 15, color: '#e2e8f0', marginBottom: 20 }}>
              Deletar este registro? Esta ação não pode ser desfeita.
            </div>
            <div style={S.mFooter}>
              <button style={S.btnCancel} onClick={() => setConfirmDel(null)}>Cancelar</button>
              <button style={{ ...S.btnSave, background: '#ef4444', color: '#fff' }} onClick={handleDelete}>
                Deletar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
